import React from "react";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { cn } from "@/lib/utils";
import { fashionPaths } from "@/modules";
import { CartItem } from "../store/cart-store";
import { useCart } from "../hooks/use-cart";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface CartItemCardMobileProps {
  item: CartItem;
  className?: string;
}

export const CartItemCardMobile: React.FC<CartItemCardMobileProps> = ({
  item,
  className,
}) => {
  const { t } = useTranslation();
  const { updateQuantity, removeFromCart, formatPrice: formatPriceHook } = useCart();

  // Safe formatPrice with NaN handling
  const formatPrice = (price: number | undefined | null) => {
    if (typeof price !== "number" || isNaN(price)) {
      return formatPriceHook(0);
    }
    return formatPriceHook(price);
  };

  const handleQuantityIncrease = () => {
    updateQuantity(item._id, item.quantity + 1);
  };

  const handleQuantityDecrease = () => {
    if (item.quantity > 1) {
      updateQuantity(item._id, item.quantity - 1);
    } else {
      removeFromCart(item._id);
    }
  };

  const handleRemove = () => {
    removeFromCart(item._id);
  };

  // Ürün detayını _product (Mongo ID) ile aç
  const productDetailHref = fashionPaths.product(item._product || item.productId);

  // Item price with NaN handling
  const itemPrice = typeof item.price === "number" && !isNaN(item.price) ? item.price : 0;
  const itemTotal = itemPrice * item.quantity;

  // Yeni data'dan variant bilgileri (metadata'dan al - backward compatibility)
  const variant1 =
    item.metadata?.axis1Name && item.metadata?.a1Value
      ? { name: item.metadata.axis1Name, value: item.metadata.a1Value }
      : null;
  const variant2 =
    item.metadata?.axis2Name && item.metadata?.a2Value
      ? { name: item.metadata.axis2Name, value: item.metadata.a2Value }
      : null;

  // Campaign price bilgisi (metadata'dan)
  const originalPrice = item.metadata?.originalPrice;
  const hasDiscount = originalPrice && typeof originalPrice === 'number' && originalPrice > itemPrice;
  const discountPct = hasDiscount && typeof originalPrice === 'number'
    ? Math.round(((originalPrice - itemPrice) / originalPrice) * 100)
    : 0;

  // Stock bilgisi (metadata'dan)
  const stockCount = item.metadata?.stockCount as number | undefined;
  const inStock = item.metadata?.inStock !== false;

  return (
    <div
      className={cn(
        "flex gap-3 p-3 bg-card rounded-xl border border-border/50",
        "hover:border-border hover:shadow-md transition-all duration-300",
        className
      )}
    >
      {/* Sol Taraf: Thumbnail */}
      <Link
        to={productDetailHref}
        className="block w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all duration-200"
      >
        {item.file ? (
          <img
            src={item.file}
            alt={item.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ShoppingBag className="h-6 w-6" />
          </div>
        )}
      </Link>

      {/* Sağ Taraf: Content */}
      <div className="flex-1 flex flex-col justify-between">
        {/* Marka adı ve başlık */}
        <div className="flex flex-col gap-1.5">
          <Link
            to={productDetailHref}
            className="font-semibold text-sm leading-tight line-clamp-2 cursor-pointer hover:text-primary transition-colors duration-200"
            style={{ lineHeight: "1.2" }}
          >
            {item.title}
          </Link>

          {/* Variant Badges + Stock Warning (Same Line) */}
          <div className="flex flex-wrap items-center gap-1">
            {variant1 && (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-bg-secondary dark:bg-bg-tertiary text-text-tertiary dark:text-text-tertiary border border-border-secondary dark:border-border-primary">
                {variant1.value}
              </span>
            )}
            {variant2 && (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-bg-secondary dark:bg-bg-tertiary text-text-tertiary dark:text-text-tertiary border border-border-secondary dark:border-border-primary">
                {variant2.value}
              </span>
            )}
            {inStock && stockCount && stockCount < 10 && (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
                Son {stockCount} ürün!
              </span>
            )}
          </div>

          {/* Seller Name */}
          {item.metadata?.sellerName && (
            <p className="text-xs text-muted-foreground">
              {item.metadata.sellerName}
            </p>
          )}

          {/* Price Section - Compact Layout */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {hasDiscount && originalPrice ? (
              <>
                <span className="text-xs line-through text-muted-foreground">
                  {formatPrice(originalPrice as number)}
                </span>
                <span className="text-base font-bold text-primary">
                  {formatPrice(itemTotal)}
                </span>
                <span className="px-1 py-0.5 bg-red-500 text-white rounded text-[10px] font-semibold">
                  %{discountPct}
                </span>
              </>
            ) : (
              <span className="text-base font-bold text-primary">
                {formatPrice(itemTotal)}
              </span>
            )}
          </div>
        </div>

        {/* Quantity + Remove */}
        <div className="flex justify-between items-center mt-3">
          {/* Quantity Controls */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={handleQuantityDecrease}
                className={cn(
                  "h-7 w-7 p-0 group transition-all duration-200",
                  "hover:bg-gradient-pink-purple hover:text-white hover:border-pink-500/20"
                )}
              >
                <Minus className="h-3 w-3 group-hover:animate-pulse" />
              </Button>

              <span className="w-8 text-center font-medium text-sm">
                {item.quantity}
              </span>

              <Button
                size="sm"
                variant="outline"
                onClick={handleQuantityIncrease}
                className={cn(
                  "h-7 w-7 p-0 group transition-all duration-200",
                  "hover:bg-gradient-cyan-blue hover:text-white hover:border-cyan-500/20"
                )}
              >
                <Plus className="h-3 w-3 group-hover:animate-pulse" />
              </Button>
            </div>
          </div>

          {/* Remove Button */}
          <Button
            size="sm"
            variant="outline"
            onClick={handleRemove}
            className={cn(
              "group transition-all duration-200 text-xs h-7",
              "hover:bg-red-500 hover:text-white hover:border-red-500/20",
              "text-red-600 border-red-200"
            )}
          >
            <Trash2 className="h-3 w-3 mr-1 group-hover:animate-pulse" />
            {t("cart.remove")}
          </Button>
        </div>
      </div>
    </div>
  );
};
